import {
  CardElevation,
  MarketingItem,
  ProductCardContainer,
  ProductCardContainerWithGroups,
  ProductCardDivider,
  DeprecatedProductsCardsList,
  ShadowBehavior,
  AIWrapperNonSale,
  AIWrapperSale,
} from '@wix/package-picker-components';
import React, { ReactNode } from 'react';
import { ProductCardData } from 'types';
import { Box, BoxProps } from '@wix/design-system';
import { observer } from 'mobx-react';

interface CardsContainerProps {
  productsCardData: ProductCardData[];
  shouldDisableFeature?: (feature: MarketingItem) => boolean;
  enableAIFeatures?: boolean;
  containerStyle?: BoxProps;
}

function CardsContainerComp({
  productsCardData,
  shouldDisableFeature,
  enableAIFeatures = false,
  containerStyle = {},
}: CardsContainerProps) {
  const maxFeaturesAmount: number = productsCardData
    .map((productCard) => productCard.data.features.length)
    .sort((a, b) => b - a)[0];

  function getShadowBehavior(currentProductIndex: number): ShadowBehavior {
    return productsCardData[currentProductIndex].data.isRecommended ||
      productsCardData[currentProductIndex + 1]?.data.isRecommended
      ? ShadowBehavior.Shadow
      : ShadowBehavior.Border;
  }

  return (
    <Box backgroundColor='white' width='100%' {...containerStyle}>
      <DeprecatedProductsCardsList marketingItemsAmount={maxFeaturesAmount}>
        {productsCardData.map((product, index) => {
          const { data, components } = product;
          const { features, isCurrentPlan, isRecommended, id } = data;
          const { cta, hat, price, title } = components;
          const childComponents: ReactNode[] = [];
          const paddedFeaturesAmount = maxFeaturesAmount - features.length;
          const paddedFeatures = Array(paddedFeaturesAmount).fill({ text: '' });
          const cardFeatures: MarketingItem[] = [
            ...features.map((feature) => ({
              ...feature,
              text: feature.text,
              disabled: shouldDisableFeature?.(feature),
            })),
            ...paddedFeatures,
          ];
          const shadowBehavior = getShadowBehavior(index);
          const elevated =
            isRecommended || isCurrentPlan
              ? CardElevation.Medium
              : CardElevation.None;
          if (enableAIFeatures) {
            childComponents.push(
              <ProductCardContainerWithGroups
                key={`${id}-cardContainer`}
                highlighted={isRecommended}
                elevated={elevated}
                hat={hat}
                header={title}
                pricing={price}
                cta={cta}
                marketingItems={cardFeatures}
                wrappers={{
                  ai: productsCardData.some(
                    (productItem) => productItem.data.isOnSale,
                  )
                    ? AIWrapperSale
                    : AIWrapperNonSale,
                }}
              />,
            );
          } else {
            childComponents.push(
              <ProductCardContainer
                key={`${id}-cardContainer`}
                highlighted={isRecommended}
                elevated={elevated}
                hat={hat}
                header={title}
                pricing={price}
                cta={cta}
                marketingItems={cardFeatures}
              />,
            );
          }

          if (index < productsCardData.length - 1) {
            childComponents.push(
              <ProductCardDivider
                key={`${id}-divider`}
                shadowBehavior={shadowBehavior}
              />,
            );
          }

          return childComponents;
        })}
      </DeprecatedProductsCardsList>
    </Box>
  );
}

export const CardsContainer = observer(CardsContainerComp);
